// v2 noticeboard — a roofed parish board: two sturdy chamfer-capped posts
// carrying a wide framed cork board with a reading ledge and diagonal braces
// beneath it, under a proper gabled shingle roof with a ridge beam and knob
// finials. Papers of three sizes are pinned askew on the front face (+z — it
// orients to the road) with red pins, and one weathered note hangs on the back.
// Ported 1:1 from age-of-mcp's v2/noticeboard.ts, using the library's shared
// `foundationSlab` helper.
import * as THREE from "three";
import { defineModel, foundationSlab, type Part } from "../../lib";

const POST   = "#6b4a26";   // posts and braces
const FRAME  = "#56351c";   // board frame
const CORK   = "#a87f4a";   // cork face
const ROOF   = "#5e3f24";   // shingle roof
const RIDGE  = "#4a3018";   // ridge beam and finials
const NOTE1  = "#efe6cf";   // pinned papers
const NOTE2  = "#dfe7ef";
const NOTE3  = "#e7d9b8";
const PIN    = "#b23a34";

// The two posts with their chamfer caps.
function posts(): Part[] {
  const out: Part[] = [];
  for (const x of [-0.17, 0.17]) {
    out.push(
      { geometry: new THREE.BoxGeometry(0.06, 0.5, 0.06), color: POST,
        position: [x, 0.31, 0] },
      { geometry: new THREE.BoxGeometry(0.085, 0.03, 0.085), color: RIDGE,
        position: [x, 0.575, 0] },
    );
  }
  return out;
}

// The framed cork board with its reading ledge and diagonal braces.
function board(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.48, 0.3, 0.045), color: FRAME,
      position: [0, 0.41, 0] },
    { geometry: new THREE.BoxGeometry(0.42, 0.24, 0.055), color: CORK,
      position: [0, 0.41, 0] },
    { geometry: new THREE.BoxGeometry(0.5, 0.025, 0.08), color: FRAME,
      position: [0, 0.265, 0.05] },
    { geometry: new THREE.BoxGeometry(0.04, 0.18, 0.04), color: POST,
      position: [-0.14, 0.235, 0], rotation: [0, 0, 0.6] },
    { geometry: new THREE.BoxGeometry(0.04, 0.18, 0.04), color: POST,
      position: [0.14, 0.235, 0], rotation: [0, 0, -0.6] },
  ];
}

// The gabled shingle roof: two pitched slabs, a ridge beam, knob finials.
// +theta about X drops a slab's +z eave; the -z slab mirrors it.
function roof(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.6, 0.03, 0.2), color: ROOF,
      position: [0, 0.655, 0.077], rotation: [0.62, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.6, 0.03, 0.2), color: ROOF,
      position: [0, 0.655, -0.077], rotation: [-0.62, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.62, 0.05, 0.06), color: RIDGE,
      position: [0, 0.725, 0] },
    { geometry: new THREE.SphereGeometry(0.028, 6, 5), color: RIDGE,
      position: [-0.3, 0.745, 0] },
    { geometry: new THREE.SphereGeometry(0.028, 6, 5), color: RIDGE,
      position: [0.3, 0.745, 0] },
  ];
}

// Papers pinned askew: three on the front face, one weathered on the back.
function papers(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.11, 0.13, 0.012), color: NOTE1,
      position: [-0.09, 0.44, 0.034], rotation: [0, 0, 0.08] },
    { geometry: new THREE.BoxGeometry(0.09, 0.1, 0.012), color: NOTE2,
      position: [0.05, 0.4, 0.034], rotation: [0, 0, -0.14] },
    { geometry: new THREE.BoxGeometry(0.07, 0.08, 0.012), color: NOTE3,
      position: [0.14, 0.46, 0.034], rotation: [0, 0, 0.18] },
    { geometry: new THREE.BoxGeometry(0.018, 0.018, 0.018), color: PIN,
      position: [-0.09, 0.495, 0.04] },
    { geometry: new THREE.BoxGeometry(0.018, 0.018, 0.018), color: PIN,
      position: [0.05, 0.445, 0.04] },
    { geometry: new THREE.BoxGeometry(0.018, 0.018, 0.018), color: PIN,
      position: [0.14, 0.495, 0.04] },
    { geometry: new THREE.BoxGeometry(0.1, 0.11, 0.012), color: NOTE3,
      position: [0.02, 0.42, -0.034], rotation: [0, 0, -0.1] },
  ];
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.36, 0.06)];
  if (s === 1) return [foundationSlab(0.36, 0.06), ...posts()];
  if (s === 2) return [foundationSlab(0.36, 0.06), ...posts(), ...board()];
  return [
    foundationSlab(0.36, 0.06),
    ...posts(),
    ...board(),
    ...roof(),
    ...papers(),
  ];
}

export const noticeboardV2 = defineModel({
  id: "noticeboard-v2",
  tiles: { w: 1, h: 1 },
  batchId: "fable",
  name: "Noticeboard (v2)",
  tags: ["building","noticeboard","low-poly","age-of-mcp","fable"],
  summary: "Low-poly noticeboard.",
  description: "A posting board where couriers leave written reports to be read.",
  author: "age-of-mcp",
  footprint: 0.3,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
});
